/**
 * The output sizes an uploaded image can be cropped to, shared by client and server.
 *
 * Shared rather than declared in the uploader because the browser's crop is a
 * convenience and the server's resize is the control — the same reasoning as the
 * upload ceiling in `imageUploadLimits`. A request can be made without the page,
 * so the server has to recognise the preset it is being asked for rather than
 * accept whatever width and height arrive.
 *
 * Pixel dimensions are the target the crop is rendered to; `aspect` is what the
 * crop window is locked to and is derived from them, so the two can never
 * disagree.
 */

export type ImagePresetKey =
  | "facebook_wall"
  | "instagram_post"
  | "flyer_letter"
  | "half_page"
  | "free";

export type ImagePreset = {
  key: ImagePresetKey;
  label: string;
  /** What this size is for, shown under the label. */
  hint: string;
  /**
   * Target output size in pixels. Null for free-form, which keeps whatever the
   * crop window produces.
   */
  width: number | null;
  height: number | null;
};

/**
 * Print sizes are rendered at 300 DPI, which is what a printer wants and what
 * makes an 8.5x11 usable as an actual ad rather than a screen mock-up. That is
 * why the letter preset is 2550x3300 and not something screen-sized.
 */
const PRINT_DPI = 300;
const inches = (value: number) => Math.round(value * PRINT_DPI);

export const IMAGE_PRESETS: ImagePreset[] = [
  {
    key: "facebook_wall",
    label: "Facebook wall",
    hint: "1200 × 630 · shared post and link preview",
    width: 1200,
    height: 630,
  },
  {
    key: "instagram_post",
    label: "Instagram post",
    hint: "1080 × 1080 · square feed post",
    width: 1080,
    height: 1080,
  },
  {
    key: "flyer_letter",
    label: "Flyer / full page",
    hint: `8.5" × 11" portrait · ${PRINT_DPI} DPI print`,
    width: inches(8.5),
    height: inches(11),
  },
  {
    key: "half_page",
    label: "Half page",
    hint: `8.5" × 5.5" landscape · ${PRINT_DPI} DPI print`,
    width: inches(8.5),
    height: inches(5.5),
  },
  {
    key: "free",
    label: "Free-form",
    hint: "No fixed ratio · crop to whatever you need",
    width: null,
    height: null,
  },
];

export const IMAGE_PRESETS_BY_KEY: Record<ImagePresetKey, ImagePreset> = Object.fromEntries(
  IMAGE_PRESETS.map(preset => [preset.key, preset]),
) as Record<ImagePresetKey, ImagePreset>;

/**
 * The ratio the crop window locks to, or undefined for free-form.
 *
 * Derived from the pixel targets rather than stored beside them: a hardcoded
 * ratio that disagreed with its own dimensions would stretch every image cropped
 * with it, and the distortion would be subtle enough to ship.
 */
export function presetAspect(key: ImagePresetKey): number | undefined {
  const preset = IMAGE_PRESETS_BY_KEY[key];
  if (!preset?.width || !preset.height) return undefined;
  return preset.width / preset.height;
}

/** Whether a value from a request names a preset we actually offer. */
export function isImagePresetKey(value: unknown): value is ImagePresetKey {
  return typeof value === "string" && value in IMAGE_PRESETS_BY_KEY;
}
